import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mainSelectedTab } from "../../OfficeTab/atoms";
import { OfficeTabs } from "../../OfficeTab/OfficeTabs";

import type { PaginatedTableQuery } from "../../../components/tables/CustomPaginatedTable/types";
import { permissionsSelector } from "../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../PermissionsConstants";
import { OFFICE_PERMISSIONS } from "modules/CarrierAgreementMgmt/OfficeSettings/permission";

import appHistory from "../../../utils/History";

import { addDigitalAsset } from "./api/addDigitalAsset";
import { deactivateDigitalAssets } from "./api/deactivateDigitalAssets";
import { getDigitalAssetById } from "./api/getDigitalAssetById";
import { getDigitalAssetOffices } from "./api/getDigitalAssetOffices";
import { getDigitalAssets } from "./api/getDigitalAssets";
import { getDigitalAssetsDownload } from "./api/getDigitalAssetsDownload";
import { getDigitalServices } from "./api/getDigitalServices";
import { getOnlineDirectories } from "./api/getOnlineDirectories";
import { getDigitalAssetAppleCategories } from "./api/getDigitalAssetAppleCategories";
import { getDigitalAssetAppleAppNames } from "./api/getDigitalAssetAppleAppNames";
import { updateDigitalAsset } from "./api/updateDigitalAsset";

import { toast } from "../../../components/toastNotification";

import DigitalAssetTable from "./DigitalAssetTable";
import DigitalAssetExpandedRow from "./view/DigitalAssetExpandedRow";
import DigitalAssetForm from "./form/DigitalAssetForm";

import type {
  DigitalAsset,
  DigitalAssetFormValues,
  DigitalAssetMode,
  DigitalAssetPermissions,
  DigitalAssetDownloadRow,
  OfficeOption,
} from "./types/index";

import { toApiPayload } from "./types/index";

interface DigitalAssetPageProps {
  officeCode?: string;
  officeRecord?: OfficeOption;
  viewFromOffice?: boolean;
  permissions?: DigitalAssetPermissions;
  onOpenOffice?: (officeCode: string) => void;
}

interface RouteState {
  moduleName?: string;
  recordId?: number | string;
  subModuleName?: string;

  action?: string;
  daRecordId?: number | string;
}

const DIGITAL_ASSETS_QUERY_KEY = "digital-assets";
const DIGITAL_ASSET_ROUTE = "/officesettings/digitalasset";

const getRecentRecordIdsFromState = (
  state?: RouteState | null,
): string | undefined => {
  if (!state) {
    return undefined;
  }

  const moduleName = state.moduleName?.trim().toLowerCase();

  if (
    moduleName === "digital assets" &&
    state.recordId !== undefined &&
    state.recordId !== null
  ) {
    const value = String(state.recordId).trim();

    return value !== "" ? value : undefined;
  }

  if (
    state.action === "closeFromRedirection" &&
    state.daRecordId !== undefined &&
    state.daRecordId !== null
  ) {
    const value = String(state.daRecordId).trim();

    return value !== "" ? value : undefined;
  }

  return undefined;
};

const getValidRecentActivityState = (
  state: unknown,
): RouteState | undefined => {
  const routeState = state as RouteState | undefined;

  return getRecentRecordIdsFromState(routeState) ? routeState : undefined;
};

const DigitalAssetPage = ({
  officeCode,
  officeRecord,
  viewFromOffice = false,
  permissions,
  onOpenOffice,
}: DigitalAssetPageProps) => {
  const location = useLocation<RouteState>();
  const history = useHistory();

  const queryClient = useQueryClient();
  const userPermissions = useRecoilValue(permissionsSelector);
  const setSelectedIndex = useSetRecoilState(mainSelectedTab);

  const resolvedPermissions = useMemo<DigitalAssetPermissions>(() => {
    if (permissions) {
      return permissions;
    }

    return {
      canAdd: userPermissions.includes(
        PERMISSIONS.permission_carrieragreemnentmgmt_digitalassets_add,
      ),

      canEdit: userPermissions.includes(
        PERMISSIONS.permission_carrieragreemnentmgmt_digitalassets_edit,
      ),

      canDeactivate: userPermissions.includes(
        PERMISSIONS.permission_carrieragreemnentmgmt_digitalassets_delete,
      ),

      canDownload: true,

      canViewOffice: userPermissions.includes(
        OFFICE_PERMISSIONS.permission_carrieragreemnentmgmt_office_view,
      ),
    };
  }, [permissions, userPermissions]);

  const [mode, setMode] = useState<DigitalAssetMode>("list");

  const [selectedAsset, setSelectedAsset] = useState<DigitalAsset | null>(null);

  const [selectedRows, setSelectedRows] = useState<DigitalAsset[]>([]);

  const [formReturnMode, setFormReturnMode] = useState<"list" | "view">("list");

  const [tableQuery, setTableQuery] = useState<PaginatedTableQuery>({
    pageNumber: 1,
    pageSize: 10,
    search: "",
    sortField: "createdOn",
    sortDirection: "desc",
  });

  const [recentActivityState, setRecentActivityState] = useState<
    RouteState | undefined
  >(() => {
    const outerState = getValidRecentActivityState(appHistory.location.state);

    if (outerState) {
      return outerState;
    }

    return getValidRecentActivityState(location.state);
  });

  useEffect(() => {
    const syncFromOuterHistory = (pathname: string, state: unknown) => {
      if (pathname.toLowerCase() !== DIGITAL_ASSET_ROUTE) {
        return;
      }

      const validState = getValidRecentActivityState(state);
      setRecentActivityState(validState);
    };

    syncFromOuterHistory(
      appHistory.location.pathname,
      appHistory.location.state,
    );

    const unlisten = appHistory.listen((nextLocation) => {
      syncFromOuterHistory(nextLocation.pathname, nextLocation.state);
    });

    return unlisten;
  }, []);

  useEffect(() => {
    const innerState = getValidRecentActivityState(location.state);

    if (innerState) {
      setRecentActivityState(innerState);
    }
  }, [location.state]);

  const recentRecordIds = useMemo(
    () => getRecentRecordIdsFromState(recentActivityState),
    [recentActivityState],
  );
  useEffect(() => {
    if (viewFromOffice || !recentActivityState || !recentRecordIds) {
      return;
    }

    const browserPath = window.location.pathname.toLowerCase();

    if (browserPath === DIGITAL_ASSET_ROUTE) {
      return;
    }

    appHistory.replace({
      pathname: DIGITAL_ASSET_ROUTE,
      state: recentActivityState,
    });
  }, [viewFromOffice, recentActivityState, recentRecordIds, location.pathname]);

  useEffect(() => {
    if (!recentRecordIds) {
      return;
    }

    setMode("list");
    setSelectedAsset(null);
    setSelectedRows([]);

    setTableQuery((current) => {
      if (current.pageNumber === 1 && (current.search ?? "") === "") {
        return current;
      }

      return {
        ...current,
        pageNumber: 1,
        search: "",
      };
    });
  }, [recentRecordIds]);

  const effectiveOfficeCode =
    viewFromOffice && officeCode ? officeCode : undefined;

  const assetsQuery = useQuery(
    [
      DIGITAL_ASSETS_QUERY_KEY,
      tableQuery,
      effectiveOfficeCode,
      recentRecordIds,
    ],
    () =>
      getDigitalAssets({
        ...tableQuery,
        officeCode: effectiveOfficeCode,
        recordIds: recentRecordIds,
      }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  );

  const officesQuery = useQuery(
    ["digital-asset-office-options"],
    async () => getDigitalAssetOffices(),
    {
      refetchOnWindowFocus: false,
    },
  );

  const servicesQuery = useQuery(
    ["digital-asset-services"],
    async () => {
      const services = await getDigitalServices();

      return services.map(({ serviceName }) => ({
        label: serviceName,
        value: serviceName,
      }));
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const directoriesQuery = useQuery(
    ["digital-asset-directories"],
    async () => {
      const directories = await getOnlineDirectories();

      return directories.map(({ brandName }) => ({
        label: brandName,
        value: brandName,
      }));
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const appleCategoriesQuery = useQuery(
    ["digital-asset-apple-categories"],
    async () => {
      const categories = await getDigitalAssetAppleCategories();

      return categories.map(
        ({ digitalAssetAppleCategoryId, digitalAssetAppleCategoryName }) => ({
          label: digitalAssetAppleCategoryName,
          value: String(digitalAssetAppleCategoryId),
        }),
      );
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const appleAppNamesQuery = useQuery(
    ["digital-asset-apple-app-names"],
    async () => {
      const appNames = await getDigitalAssetAppleAppNames();

      return appNames.map(({ appleAppNameId, appleAppName }) => ({
        label: appleAppName,
        value: String(appleAppNameId),
      }));
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const closeView = useCallback(() => {
    setMode("list");
    setSelectedAsset(null);
    setFormReturnMode("list");
  }, []);

  const closeForm = useCallback(() => {
    if (formReturnMode === "view" && selectedAsset) {
      setMode("view");
      return;
    }

    setMode("list");
    setSelectedAsset(null);
    setFormReturnMode("list");
  }, [formReturnMode, selectedAsset]);

  const refreshAssets = useCallback(async () => {
    await queryClient.invalidateQueries(DIGITAL_ASSETS_QUERY_KEY);

    await queryClient.invalidateQueries(["digital-asset-office-options"]);
  }, [queryClient]);

  const saveMutation = useMutation(
    async ({
      values,
      edit,
    }: {
      values: DigitalAssetFormValues;
      edit: boolean;
    }) => {
      const payload = toApiPayload(values);

      return edit ? updateDigitalAsset(payload) : addDigitalAsset(payload);
    },
    {
      onSuccess: async (_result, variables) => {
        await refreshAssets();

        toast.fire("Success", "Digital Asset saved successfully.", "success");

        if (formReturnMode === "view" && selectedAsset) {
          const id = variables.values.id ?? selectedAsset.id;

          if (variables.edit && id) {
            try {
              const refreshedAsset = await getDigitalAssetById(id);

              if (refreshedAsset) {
                setSelectedAsset(refreshedAsset);
              }
            } catch {}
          }

          setMode("view");
          return;
        }

        setMode("list");
        setSelectedAsset(null);
        setFormReturnMode("list");
      },

      onError: (error: unknown) => {
        const message =
          error instanceof Error ? error.message : "Cannot save Digital Asset.";

        toast.fire(
          message === "Digital Asset already exists for this Office."
            ? "Duplicate Digital Asset"
            : "Saving Failed",
          message,
          "error",
        );
      },
    },
  );

  const deactivateMutation = useMutation(
    (ids: number[]) => deactivateDigitalAssets(ids),
    {
      onSuccess: async () => {
        await refreshAssets();

        setSelectedRows([]);

        toast.fire(
          "Success",
          "Digital Assets deactivated successfully.",
          "success",
        );
      },

      onError: () =>
        toast.fire(
          "Deactivation Failed",
          "Cannot deactivate Digital Assets.",
          "error",
        ),
    },
  );

  const downloadMutation = useMutation(
    () =>
      getDigitalAssetsDownload<DigitalAssetDownloadRow>({
        search: tableQuery.search ?? "",
        officeCode: effectiveOfficeCode ?? "",
        recordIds: recentRecordIds ?? "",
      }),
    {
      onError: () =>
        toast.fire(
          "Download Digital Assets",
          "Server Error: Cannot download digital assets.",
          "error",
        ),
    },
  );

  const loadAsset = useCallback(
    async (asset: DigitalAsset, nextMode: "view" | "edit") => {
      try {
        const details = await getDigitalAssetById(asset.id);

        if (!details) {
          toast.fire(
            "Not Found",
            "Digital Asset details were not found.",
            "warning",
          );

          return;
        }

        setSelectedAsset(details);
        setMode(nextMode);
      } catch {
        toast.fire(
          "Loading Failed",
          "Cannot retrieve Digital Asset details.",
          "error",
        );
      }
    },
    [],
  );

  const handleOpenOffice = useCallback(
    (targetOfficeCode: string) => {
      if (onOpenOffice) {
        onOpenOffice(targetOfficeCode);
        return;
      }

      const url = "/officesettings/officesetting";

      history.push({
        pathname: url,
        state: {
          pathname: url,
          officeCode: targetOfficeCode,
          action: "DigitalAsset",
        },
      });

      const index = OfficeTabs.findIndex((tab) => tab.to === url);

      if (index >= 0) {
        setSelectedIndex(index);
      }
    },
    [history, onOpenOffice, setSelectedIndex],
  );

  if (mode === "add" || mode === "edit") {
    return (
      <DigitalAssetForm
        asset={mode === "edit" ? selectedAsset : null}
        mode={mode}
        offices={officesQuery.data ?? []}
        services={servicesQuery.data ?? []}
        onlineDirectories={directoriesQuery.data ?? []}
        appleCategories={appleCategoriesQuery.data ?? []}
        appleAppNames={appleAppNamesQuery.data ?? []}
        isLoadingOptions={
          officesQuery.isLoading ||
          servicesQuery.isLoading ||
          directoriesQuery.isLoading ||
          appleCategoriesQuery.isLoading ||
          appleAppNamesQuery.isLoading
        }
        isSaving={saveMutation.isLoading}
        initialOfficeCode={viewFromOffice ? officeCode : undefined}
        currentOffice={viewFromOffice ? officeRecord : undefined}
        lockOffice={viewFromOffice && mode === "add"}
        onCancel={closeForm}
        onSubmit={(values) =>
          saveMutation.mutate({
            values,
            edit: mode === "edit",
          })
        }
      />
    );
  }

  if (mode === "view" && selectedAsset) {
    return (
      <DigitalAssetExpandedRow
        asset={selectedAsset}
        permissions={resolvedPermissions}
        onAdd={() => {
          setFormReturnMode("view");
          setMode("add");
        }}
        onEdit={() => {
          setFormReturnMode("view");
          setMode("edit");
        }}
        onClose={closeView}
        onOpenOffice={handleOpenOffice}
      />
    );
  }

  return (
    <DigitalAssetTable
      assets={assetsQuery.data?.data ?? []}
      totalCount={assetsQuery.data?.totalCount ?? 0}
      query={tableQuery}
      selectedRows={selectedRows}
      permissions={resolvedPermissions}
      viewFromOffice={viewFromOffice}
      isLoading={assetsQuery.isFetching || deactivateMutation.isLoading}
      isDownloading={downloadMutation.isLoading}
      onQueryChange={setTableQuery}
      onSelectionChange={setSelectedRows}
      onDownload={() => downloadMutation.mutateAsync()}
      onAdd={() => {
        setFormReturnMode("list");
        setSelectedAsset(null);
        setMode("add");
      }}
      onView={(asset) => {
        setFormReturnMode("list");

        void loadAsset(asset, "view");
      }}
      onEdit={(asset) => {
        setFormReturnMode("list");

        void loadAsset(asset, "edit");
      }}
      onDeactivate={(ids) => deactivateMutation.mutate(ids)}
      onOpenOffice={handleOpenOffice}
    />
  );
};

export default DigitalAssetPage;
